import { writable } from 'svelte/store';
import SuperTokens from 'supertokens-web-js';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import Session from 'supertokens-web-js/recipe/session';
import { tImmediate } from '../i18n/index.js';

const apiDomain = import.meta.env.VITE_API_URL || window.location.origin;

// Initialize SuperTokens
SuperTokens.init({
  appInfo: {
    appName: 'WEBDEPLOY SdI FatturaPA',
    apiDomain,
    websiteDomain: import.meta.env.VITE_WEBSITE_DOMAIN || window.location.origin,
    apiBasePath: '/api/auth',
    websiteBasePath: '/auth',
  },
  recipeList: [
    EmailPassword.init(),
    Session.init(),
  ],
});

export const authStore = writable({
  isAuthenticated: false,
  user: null,
  loading: true,
  // True only when the server is unreachable (network error), so the shell can
  // show a "service unavailable" screen instead of dumping the user on login.
  serverDown: false,
});

/**
 * Check current authentication status against the server.
 */
export async function checkAuthStatus() {
  try {
    authStore.update(state => ({ ...state, loading: true, serverDown: false }));

    const response = await fetch(`${apiDomain}/api/auth/user`, {
      credentials: 'include',
    });

    if (response.ok) {
      const user = await response.json();
      authStore.set({ isAuthenticated: true, user, loading: false, serverDown: false });
    } else {
      try { await Session.signOut(); } catch {}
      authStore.set({ isAuthenticated: false, user: null, loading: false, serverDown: false });
    }
  } catch (error) {
    // Network/transport failure — the server is unreachable, not a real logout.
    console.error('Auth status check failed:', error);
    authStore.set({ isAuthenticated: false, user: null, loading: false, serverDown: true });
  }
}

/**
 * Login with email and password.
 */
export async function login(email, password) {
  try {
    authStore.update(state => ({ ...state, loading: true }));

    const response = await EmailPassword.signIn({
      formFields: [
        { id: 'email', value: email },
        { id: 'password', value: password },
      ],
    });

    if (response.status === 'OK') {
      await checkAuthStatus();
      return { success: true };
    } else {
      authStore.update(state => ({ ...state, loading: false }));
      return {
        success: false,
        error: response.status === 'WRONG_CREDENTIALS_ERROR'
          ? tImmediate('auth.wrongCredentials')
          : tImmediate('auth.loginError'),
      };
    }
  } catch (error) {
    console.error('Login failed:', error);
    authStore.update(state => ({ ...state, loading: false }));
    return { success: false, error: tImmediate('auth.connectionError') };
  }
}

/**
 * Logout current user.
 */
export async function logout() {
  try {
    authStore.update(state => ({ ...state, loading: true }));
    await Session.signOut();
    authStore.set({ isAuthenticated: false, user: null, loading: false });
  } catch (error) {
    console.error('Logout failed:', error);
    authStore.set({ isAuthenticated: false, user: null, loading: false });
  }
}

/**
 * Change password.
 */
export async function changePassword(oldPassword, newPassword) {
  try {
    const response = await fetch(`${apiDomain}/api/auth/change-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldPassword, newPassword }),
    });

    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || tImmediate('auth.passwordChangeError') };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: tImmediate('auth.connectionError') };
  }
}
